/**
 * Pinia store for device-level automation bindings.
 * Lazy-loads per device from GET /device-automations/devices/:id/bindings
 * and caches the results to avoid repeat requests.
 */

import { defineStore } from 'pinia';
import { ref } from 'vue';
import { apiFetch } from '../services/api';

/**
 * An automation's config bag: the fields its own editor writes, whose shape
 * depends on the automation type.
 */
export interface DeviceAutomationConfig {
  [key: string]: unknown;
}

export interface DeviceAutomationBinding {
  deviceId?: string;
  automationId: string;
  automationName: string;
  automationType: string;
  category: string | null;
  enabled: boolean;
  /** The role this device plays in the automation: "trigger" | "output" | "inhibit" | etc. */
  bindingRole: string;
  valueKey: string;
  valueName: string;
}

export interface DeviceAutomationSummary {
  id: string;
  name: string;
  automationType: string;
  icon: string;
  category: string | null;
  enabled: boolean;
  config: DeviceAutomationConfig;
  deviceId: string;
  createdAt?: string;
  updatedAt?: string;
  bindingCount?: number;
}

/**
 * A binding row as the hub sends it: snake_case on most endpoints, camelCase on
 * others — `normalizeBinding` reads whichever the row carries.
 */
interface DeviceAutomationBindingRow {
  device_id?: string;
  deviceId?: string;
  automation_id?: string;
  automationId?: string;
  automation_name?: string;
  automationName?: string;
  automation_type?: string;
  automationType?: string;
  category?: string | null;
  enabled?: boolean | null;
  binding_role?: string;
  bindingRole?: string;
  value_key?: string;
  valueKey?: string;
  value_name?: string;
  valueName?: string;
}

/** A summary row as the hub sends it (the count is the one snake_case field). */
interface DeviceAutomationSummaryRow {
  id: string;
  name: string;
  automationType: string;
  icon?: string | null;
  category?: string | null;
  enabled?: boolean | null;
  config?: DeviceAutomationConfig;
  deviceId: string;
  createdAt?: string;
  updatedAt?: string;
  binding_count?: number;
  bindingCount?: number;
}

export const useDeviceAutomationsStore = defineStore('deviceAutomations', () => {
  /** Cache: deviceId → list of bindings */
  const bindingCache = ref<Record<string, DeviceAutomationBinding[]>>({});
  /** Cache: deviceId → automation summaries (includes config) */
  const summaryCache = ref<Record<string, DeviceAutomationSummary[]>>({});
  const bindingInFlight = new Map<string, Promise<void>>();
  const summaryInFlight = new Map<string, Promise<void>>();
  const queuedBindingIds = new Set<string>();
  let queuedBindingForce = false;
  let bindingBatchTimer: number | null = null;

  function uniqueIds(ids: Iterable<unknown>): string[] {
    return Array.from(
      new Set(
        Array.from(ids)
          .map((id) => String(id ?? '').trim())
          .filter(Boolean),
      ),
    );
  }

  function normalizeBinding(row: DeviceAutomationBindingRow, fallbackDeviceId?: string): DeviceAutomationBinding {
    return {
      deviceId: row.device_id ?? row.deviceId ?? fallbackDeviceId,
      automationId: row.automation_id ?? row.automationId ?? '',
      automationName: row.automation_name ?? row.automationName ?? '',
      automationType: row.automation_type ?? row.automationType ?? '',
      category: row.category ?? null,
      enabled: row.enabled ?? true,
      bindingRole: row.binding_role ?? row.bindingRole ?? '',
      valueKey: row.value_key ?? row.valueKey ?? '',
      valueName: row.value_name ?? row.valueName ?? '',
    };
  }

  function normalizeSummary(row: DeviceAutomationSummaryRow): DeviceAutomationSummary {
    return {
      id: row.id,
      name: row.name,
      automationType: row.automationType,
      icon: row.icon ?? 'settings',
      category: row.category ?? null,
      enabled: row.enabled ?? true,
      config: row.config ?? {},
      deviceId: row.deviceId,
      createdAt: row.createdAt,
      updatedAt: row.updatedAt,
      bindingCount: row.binding_count ?? row.bindingCount ?? 0,
    };
  }

  /**
   * Fetch and cache automations for a device.
   * Safe to call repeatedly – skips network if already in cache.
   */
  async function fetchDeviceAutomations(deviceId: string, force = false): Promise<void> {
    if (!force && bindingCache.value[deviceId] !== undefined) return;
    if (!force && bindingInFlight.has(deviceId)) return bindingInFlight.get(deviceId)!;

    const request = (async () => {
      try {
        const rows: DeviceAutomationBindingRow[] = await apiFetch(`/device-automations/devices/${deviceId}/bindings`);

        if (!Array.isArray(rows)) {
          console.error('[deviceAutomations] Expected array, got:', rows);
          bindingCache.value[deviceId] = [];
          return;
        }

        bindingCache.value[deviceId] = rows.map((row) => normalizeBinding(row, deviceId));

        console.debug(
          `[deviceAutomations] Loaded ${bindingCache.value[deviceId].length} binding(s) for device ${deviceId}`,
        );
      } catch (err) {
        console.error(`[deviceAutomations] Failed to fetch automations for device ${deviceId}:`, err);
        // Keep any previously loaded rows (e.g. a background refresh failure must
        // not blank the widgets' automation badges); only seed an empty entry so
        // we don't hammer the server on every re-render.
        if (bindingCache.value[deviceId] === undefined) {
          bindingCache.value[deviceId] = [];
        }
      }
    })().finally(() => {
      bindingInFlight.delete(deviceId);
    });

    bindingInFlight.set(deviceId, request);
    return request;
  }

  async function fetchDeviceAutomationsBulk(deviceIds: Iterable<unknown>, force = false): Promise<void> {
    const ids = uniqueIds(deviceIds);
    if (ids.length === 0) return;

    const idsToFetch = ids.filter((id) => force || (bindingCache.value[id] === undefined && !bindingInFlight.has(id)));
    const pending = ids
      .map((id) => bindingInFlight.get(id))
      .filter((request): request is Promise<void> => Boolean(request));

    if (idsToFetch.length > 0) {
      const request = (async () => {
        try {
          const rows: DeviceAutomationBindingRow[] = await apiFetch(
            `/device-automations/bindings?deviceIds=${encodeURIComponent(idsToFetch.join(','))}`,
          );

          const grouped: Record<string, DeviceAutomationBinding[]> = Object.fromEntries(
            idsToFetch.map((id) => [id, []]),
          );

          if (Array.isArray(rows)) {
            for (const row of rows) {
              const binding = normalizeBinding(row);
              if (!binding.deviceId) continue;
              if (!grouped[binding.deviceId]) grouped[binding.deviceId] = [];
              grouped[binding.deviceId].push(binding);
            }
          }

          for (const id of idsToFetch) {
            bindingCache.value[id] = grouped[id] ?? [];
          }
        } catch (err) {
          console.error('[deviceAutomations] Failed to fetch bulk automation bindings:', err);
          // Preserve rows loaded earlier — a failed refresh must not blank the badges.
          for (const id of idsToFetch) {
            if (bindingCache.value[id] === undefined) {
              bindingCache.value[id] = [];
            }
          }
        }
      })().finally(() => {
        for (const id of idsToFetch) {
          bindingInFlight.delete(id);
        }
      });

      for (const id of idsToFetch) {
        bindingInFlight.set(id, request);
      }
      pending.push(request);
    }

    if (pending.length > 0) await Promise.allSettled(pending);
  }

  function queueDeviceAutomations(deviceId: string, force = false): void {
    const id = String(deviceId ?? '').trim();
    if (!id) return;

    if (!force && bindingCache.value[id] !== undefined) return;
    if (!force && bindingInFlight.has(id)) return;

    queuedBindingIds.add(id);
    queuedBindingForce = queuedBindingForce || force;

    if (bindingBatchTimer !== null) {
      return;
    }

    bindingBatchTimer = window.setTimeout(() => {
      const idsToFetch = Array.from(queuedBindingIds);
      const forceFetch = queuedBindingForce;

      queuedBindingIds.clear();
      queuedBindingForce = false;
      bindingBatchTimer = null;

      if (idsToFetch.length > 0) {
        void fetchDeviceAutomationsBulk(idsToFetch, forceFetch);
      }
    }, 0);
  }

  async function fetchDeviceAutomationSummaries(deviceId: string, force = false): Promise<void> {
    if (!force && summaryCache.value[deviceId] !== undefined) return;
    if (!force && summaryInFlight.has(deviceId)) return summaryInFlight.get(deviceId)!;

    const request = (async () => {
      try {
        const rows: DeviceAutomationSummaryRow[] = await apiFetch(
          `/device-automations?deviceId=${encodeURIComponent(deviceId)}`,
        );

        if (!Array.isArray(rows)) {
          console.error('[deviceAutomations] Expected automation summary array, got:', rows);
          summaryCache.value[deviceId] = [];
          return;
        }

        summaryCache.value[deviceId] = rows.map(normalizeSummary);

        console.debug(
          `[deviceAutomations] Loaded ${summaryCache.value[deviceId].length} automation summary row(s) for device ${deviceId}`,
        );
      } catch (err) {
        console.error(`[deviceAutomations] Failed to fetch automation summaries for device ${deviceId}:`, err);
        // Preserve previously loaded summaries (see bindings fetch above).
        if (summaryCache.value[deviceId] === undefined) {
          summaryCache.value[deviceId] = [];
        }
      }
    })().finally(() => {
      summaryInFlight.delete(deviceId);
    });

    summaryInFlight.set(deviceId, request);
    return request;
  }

  async function fetchDeviceAutomationSummariesBulk(deviceIds: Iterable<unknown>, force = false): Promise<void> {
    const ids = uniqueIds(deviceIds);
    if (ids.length === 0) return;

    const idsToFetch = ids.filter((id) => force || (summaryCache.value[id] === undefined && !summaryInFlight.has(id)));
    const pending = ids
      .map((id) => summaryInFlight.get(id))
      .filter((request): request is Promise<void> => Boolean(request));

    if (idsToFetch.length > 0) {
      const request = (async () => {
        try {
          const rows: DeviceAutomationSummaryRow[] = await apiFetch(
            `/device-automations?deviceIds=${encodeURIComponent(idsToFetch.join(','))}`,
          );

          const grouped: Record<string, DeviceAutomationSummary[]> = Object.fromEntries(
            idsToFetch.map((id) => [id, []]),
          );

          if (Array.isArray(rows)) {
            for (const row of rows) {
              const summary = normalizeSummary(row);
              if (!grouped[summary.deviceId]) grouped[summary.deviceId] = [];
              grouped[summary.deviceId].push(summary);
            }
          }

          for (const id of idsToFetch) {
            summaryCache.value[id] = grouped[id] ?? [];
          }
        } catch (err) {
          console.error('[deviceAutomations] Failed to fetch bulk automation summaries:', err);
          // Preserve rows loaded earlier — a failed refresh must not blank the UI.
          for (const id of idsToFetch) {
            if (summaryCache.value[id] === undefined) {
              summaryCache.value[id] = [];
            }
          }
        }
      })().finally(() => {
        for (const id of idsToFetch) {
          summaryInFlight.delete(id);
        }
      });

      for (const id of idsToFetch) {
        summaryInFlight.set(id, request);
      }
      pending.push(request);
    }

    if (pending.length > 0) await Promise.allSettled(pending);
  }

  /** Return cached bindings (or empty array if not yet loaded). */
  function getDeviceAutomations(deviceId: string): DeviceAutomationBinding[] {
    return bindingCache.value[deviceId] ?? [];
  }

  function getDeviceAutomationSummaries(deviceId: string): DeviceAutomationSummary[] {
    return summaryCache.value[deviceId] ?? [];
  }

  /** Invalidate cache entry so next call will re-fetch. */
  function invalidate(deviceId: string): void {
    delete bindingCache.value[deviceId];
    delete summaryCache.value[deviceId];
  }

  /**
   * Call right after an automation is created / updated / deleted / toggled
   * OUTSIDE the store (device settings modals, automations list, wizards, …).
   *
   * Every device that already has cached rows is refreshed in the background
   * with one bulk request per cache, so mounted widgets pick up the change
   * (automation badges appearing / disappearing, enabled state, mode labels)
   * without reloading the whole interface. The previously cached rows stay
   * visible while the refresh runs — no flicker and no empty badges.
   *
   * A full refresh is deliberate: an automation may bind values across several
   * devices (e.g. motion sensor → light), and edit/delete flows don't tell us
   * which other devices are affected.
   */
  function notifyAutomationsMutated(): void {
    const bindingIds = Object.keys(bindingCache.value);
    if (bindingIds.length > 0) {
      void fetchDeviceAutomationsBulk(bindingIds, true);
    }

    const summaryIds = Object.keys(summaryCache.value);
    if (summaryIds.length > 0) {
      void fetchDeviceAutomationSummariesBulk(summaryIds, true);
    }
  }

  /** Clear entire cache. */
  function clear(): void {
    bindingCache.value = {};
    summaryCache.value = {};
    bindingInFlight.clear();
    summaryInFlight.clear();
    queuedBindingIds.clear();
    queuedBindingForce = false;
  }

  /**
   * Reset all caches — call when switching hubs (local login, cloud connect,
   * logout). Binding/summary caches are keyed by deviceId, which can COLLIDE
   * across hubs (e.g. a restored backup shares device IDs), so stale data from
   * a previous hub would otherwise show up on the new hub's devices.
   */
  function resetForHubSwitch(): void {
    clear();
  }

  return {
    fetchDeviceAutomations,
    fetchDeviceAutomationsBulk,
    queueDeviceAutomations,
    fetchDeviceAutomationSummaries,
    fetchDeviceAutomationSummariesBulk,
    getDeviceAutomations,
    getDeviceAutomationSummaries,
    invalidate,
    notifyAutomationsMutated,
    clear,
    resetForHubSwitch,
  };
});
